import React from 'react';
import { Calendar, Award, TrendingUp, Target, Music2, Brain, Timer, BarChart2, Heart, Mountain } from 'lucide-react';
import { motion } from 'framer-motion';

const StatBadge = ({ icon: Icon, value, label }) => (
  <div className="flex items-center gap-2">
    <Icon className="w-4 h-4 text-white/70" />
    <div className="text-sm">
      <span className="font-medium">{value}</span>
      {label && <span className="text-white/70 ml-1">{label}</span>}
    </div>
  </div>
);

const ActivityCard = ({ icon: Icon, title, subtitle, timestamp, type, children }) => (
  <motion.div 
    className="relative"
    initial={{ x: -20, opacity: 0 }}
    animate={{ x: 0, opacity: 1 }}
  >
    <div className="glass p-4 rounded-xl space-y-4">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl glass flex items-center justify-center ${
            type === 'achievement' ? 'bg-yellow-500/20 text-yellow-400' :
            type === 'run' ? 'bg-green-500/20 text-green-400' :
            type === 'music' ? 'bg-blue-500/20 text-blue-400' : 
            'bg-purple-500/20 text-purple-400'
          }`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-lg leading-tight">{title}</h3>
            <p className="text-sm text-white/70">{subtitle}</p>
          </div>
        </div>
        <span className="text-xs font-medium px-2 py-1 rounded-full glass">{timestamp}</span>
      </div>
      {children}
    </div>
  </motion.div>
);

export const Dashboard = () => {
  return (
    <div className="space-y-6">
      {/* Weekly Summary */}
      <motion.div 
        className="glass p-6 rounded-xl"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
      >
        <h2 className="text-xl font-semibold mb-4">This Week</h2>
        <div className="grid grid-cols-2 gap-4">
          <div className="glass p-4 rounded-lg">
            <p className="text-3xl font-bold bg-gradient-to-r from-green-400 to-emerald-500 bg-clip-text text-transparent">23.4km</p>
            <p className="text-sm text-white/70">Total Distance</p>
          </div>
          <div className="glass p-4 rounded-lg">
            <p className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">3</p>
            <p className="text-sm text-white/70">Runs Completed</p>
          </div>
        </div>
      </motion.div>

      {/* Next Run Suggestion */}
      <motion.div 
        className="glass p-6 rounded-xl"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.1 }}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-gradient-to-br from-orange-400 to-pink-500">
            <Target className="w-6 h-6" />
          </div>
          <h2 className="text-xl font-semibold">Suggested Next Run</h2>
        </div>
        <div className="space-y-3">
          <p className="text-lg font-semibold text-white/90">Recovery Run • 5km</p>
          <p className="text-sm text-white/70">Based on your recent 10km personal best, we recommend an easy-paced recovery run.</p>
          <div className="flex gap-2">
            <button className="glass px-4 py-2 rounded-lg text-sm font-medium hover:bg-white/20 transition-colors">
              Start Run
            </button>
            <button className="glass px-4 py-2 rounded-lg text-sm font-medium text-white/70 hover:bg-white/20 transition-colors">
              Adjust Plan
            </button>
          </div>
        </div>
      </motion.div>

      {/* Activity Feed */}
      <motion.div 
        className="glass p-6 rounded-xl"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        <h2 className="text-xl font-semibold mb-6">Activity Feed</h2>
        
        <div className="space-y-4">
          <ActivityCard
            icon={Timer}
            type="run"
            title="Evening Run"
            subtitle="10km • 48:23"
            timestamp="Today"
          >
            <div className="grid grid-cols-3 gap-3">
              <StatBadge icon={Timer} value="4:50" label="/km" />
              <StatBadge icon={Mountain} value="127m" />
              <StatBadge icon={Heart} value="162" label="bpm" />
            </div>
            <div className="mt-3 p-3 glass rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Brain className="w-4 h-4 text-purple-400" />
                <span className="text-sm font-medium">Coach's Note</span>
              </div>
              <p className="text-sm text-white/70">
                Excellent pacing throughout the run. Your heart rate stayed in the optimal zone for endurance building.
              </p>
            </div>
          </ActivityCard>

          <ActivityCard
            icon={Music2}
            type="music"
            title="Workout Mix Updated"
            subtitle="New songs added to match your pace"
            timestamp="2h ago"
          >
            <div className="flex items-center justify-between p-3 glass rounded-lg">
              <div className="flex items-center gap-3">
                <Music2 className="w-4 h-4 text-blue-400" />
                <div>
                  <p className="font-medium">Running Up That Hill</p>
                  <p className="text-xs text-white/70">Kate Bush • 170 BPM</p>
                </div>
              </div>
              <span className="text-xs px-2 py-1 rounded-full glass">Now Playing</span>
            </div>
          </ActivityCard>

          <ActivityCard
            icon={Award}
            type="achievement"
            title="30 Day Streak!"
            subtitle="You've been consistently running for a month"
            timestamp="Yesterday"
          >
            <div className="flex items-center gap-3 p-3 glass rounded-lg">
              <Award className="w-6 h-6 text-yellow-400" />
              <div>
                <p className="font-medium text-yellow-300">Consistency Champion</p>
                <p className="text-sm text-white/70">
                  Keep up the amazing work! 🎉
                </p>
              </div>
            </div>
          </ActivityCard>
        </div>
      </motion.div>
    </div>
  );
};